import {
  makeStyles,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import React, { useContext, useState } from 'react'
import {
  BooleanField,
  BooleanInput,
  Datagrid,
  DateField,
  DateInput,
  Filter,
  FilterButton,
  FilterContext,
  ListProps,
  Pagination,
  ReferenceField,
  sanitizeListRestProps,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useListContext,
  useMutation,
  useRefresh,
  useTranslate,
} from 'react-admin'
import { useHistory } from 'react-router'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import CachedIcon from '@mui/icons-material/Cached'
import { Authority } from '../../core/auth/Authority'
import { ResourceName } from '../../core/ResourceName'
import syncLogsProvider from '../../core/sync-logs/sync-log.provider'
import cloneOrCreateElement from '../common/cloneOrCreateElement'
import Button from '../common/customized-mui-components/Button'
import List from '../common/customized-ra-components/List'
import { useHasAuthority } from '../hooks/useHasAuthority'
import { CreateRecurringJobButton } from '../common/CreateRecurringJobButton'

const useStyles = makeStyles({
  expand: {
    display: 'flex',
    flexDirection: 'row',
  },
  buttonToMenuItem: {
    padding: '0 !important',
    margin: '0 !important',
    minWidth: '120px',
    textTransform: 'none',
    font: 'inherit !important',
    background: 'none !important',
    boxShadow: 'none !important',
    color: 'inherit !important',
    justifyContent: 'left',
  },
})

const SyncLogsExpand = ({ ...props }) => {
  const hasAuthority = useHasAuthority()
  const classes = useStyles()

  return (
    <SimpleShowLayout {...props} className={classes.expand}>
      {hasAuthority(Authority.VIEW_USERS) && (
        <ReferenceField
          source="createdById"
          reference={ResourceName.USERS}
          link="show"
        >
          <TextField source="userName" />
        </ReferenceField>
      )}
      {hasAuthority(Authority.VIEW_USERS) && (
        <ReferenceField
          source="modifiedById"
          reference={ResourceName.USERS}
          link={hasAuthority(Authority.VIEW_USERS) ? 'show' : false}
        >
          <TextField source="userName" />
        </ReferenceField>
      )}
      <DateField source="modifiedAt" />
    </SimpleShowLayout>
  )
}

const SyncLogsFilters = ({ ...props }) => (
  <Filter {...props}>
    <BooleanInput source="isSuccessfull" defaultValue />
    <DateInput source="createdFrom" />
    <DateInput source="createdTo" />
  </Filter>
)

const SyncLogsActions = ({ ...props }) => {
  const { className, filters: filtersProp, ...rest } = props
  const { displayedFilters, filterValues, showFilter, refetch } =
    useListContext(props)
  const resource = ResourceName.SYNC_LOGS
  const filters = useContext(FilterContext) || filtersProp
  const hasAuthority = useHasAuthority()
  const [anchorEl, setAnchorEl] = useState(null)
  const [mutate] = useMutation()
  const translate = useTranslate()
  const history = useHistory()
  const refresh = useRefresh()
  const classes = useStyles()

  const handleSyncStadiumBans = async () => {
    await mutate(
      {
        resource: ResourceName.SYNC_LOGS,
        type: 'syncStadiumBans',
      },
      { returnPromise: true },
    )
    refetch()
  }

  const handleShowStadiumBans = () => {
    history.push(
      '/tickets?displayedFilters={"isBookingBanned":true}&filter={"isBookingBanned":true}',
    )
  }

  const handleSynchronizeClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleSynchronizeClose = () => {
    setAnchorEl(null)
  }

  const saveRecurringJob = async (
    recurringJobName,
    cronExpression,
    scheduledAt,
  ) => {
    await syncLogsProvider.syncStadiumBans(ResourceName.SYNC_LOGS, {
      recurringJobName,
      cronExpression,
      scheduledAt,
    })
    refresh()
  }

  return (
    <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
      <Button
        label={translate('resources.sync-logs.actions.syncStadiumBans')}
        variant="text"
        onClick={handleSynchronizeClick}
      >
        <CachedIcon fontSize="small" />
      </Button>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleSynchronizeClose}
        onClick={handleSynchronizeClose}
      >
        <MenuItem>
          <Button
            onClick={handleSyncStadiumBans}
            label={translate('resources.sync-logs.actions.synchronizeNow')}
            disabled={!hasAuthority(Authority.SYNC_STADIUM_BANS)}
            className={classes.buttonToMenuItem}
          />
        </MenuItem>
        <MenuItem>
          <CreateRecurringJobButton
            size="small"
            saveRecurringJob={saveRecurringJob}
            variant="text"
            color="primary"
            disabled={false}
            startIcon={undefined}
            label={translate('resources.sync-logs.actions.synchronizePlan')}
            className={classes.buttonToMenuItem}
          />
        </MenuItem>
      </Menu>
      <Button
        onClick={handleShowStadiumBans}
        label={translate('resources.sync-logs.actions.showStadiumBans')}
        disabled={!hasAuthority(Authority.VIEW_TICKETS)}
      >
        <ManageSearchIcon fontSize="small" />
      </Button>
      {filtersProp
        ? cloneOrCreateElement(filtersProp, {
            resource,
            showFilter,
            displayedFilters,
            filterValues,
            context: 'button',
          })
        : filters && <FilterButton />}
    </TopToolbar>
  )
}

export const SyncLogsList = (props: ListProps) => {
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const xSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  return (
    <List
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[10, 20, 50, 100, 200]} />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      filters={<SyncLogsFilters />}
      actions={<SyncLogsActions />}
      {...props}
      bulkActionButtons={false}
      exporter={false}
      empty={false}
    >
      <Datagrid
        expand={<SyncLogsExpand />}
        padding={xSmallScreen ? 'checkbox' : 'default'}
      >
        <DateField source="createdAt" />
        <BooleanField source="isSuccessfull" />
        <TextField source="fetchedBansCount" />
        {!smallScreen && <TextField source="bannedBookingsCount" />}
        {!smallScreen && <TextField source="unbannedBookingsCount" />}
      </Datagrid>
    </List>
  )
}
